import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import type { AfterViewInit, OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { map, startWith, Subject } from 'rxjs';
import { ActionHelper } from '../../../helpers/action.helper';
import { AddressHelper } from '../../../helpers/address.helper';
import { CoordinatesHelper } from '../../../helpers/coordinates.helper';
import { FormHelper } from '../../../helpers/form.helper';
import { I18N_COUNTRY, I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxAddressService } from '../../../services/onyx-address.service';
import { OnyxDictionariesService } from '../../../services/onyx-dictionaries.service';
import { OnyxToastService } from '../../../services/onyx-toast.service';
import { onyxCoordinatesValidator } from '../../../validators/onyx-coordinates.validator';
import { onyxTextFieldLengthValidator } from '../../../validators/onyx-text-field-length.validator';
import { OnyxButtonComponent } from '../../buttons/onyx-button/onyx-button.component';
import { OnyxIconButtonComponent } from '../../buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxDropdownComponent } from '../../dropdown/onyx-dropdown/onyx-dropdown.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { OnyxMapMarkerType } from '../../map/enums';
import type { OnyxMapContextMenuOptions } from '../../map/interfaces';
import { OnyxMapEvent } from '../../map/interfaces';
import { OnyxMapComponent } from '../../map/onyx-map/onyx-map.component';
import { OnyxModalComponent } from '../../modal/onyx-modal/onyx-modal.component';
import { OnyxTextFieldComponent } from '../../text-field/onyx-text-field/onyx-text-field.component';
import { OnyxToastMessage } from '../../toast/enums';
import { OnyxAddressType } from '../enums';
import type { OnyxAddress, OnyxCustomAddress } from '../interfaces';

export interface OnyxAddressPickerModalData {
  address: OnyxAddress | null;
  heading?: string;
  readonly?: boolean;
}

type FormAddress = Omit<
  OnyxCustomAddress,
  'type' | 'label' | 'coordinates' | 'name'
>;

@Component({
  selector: 'onyx-address-picker-modal',
  imports: [
    OnyxModalComponent,
    OnyxButtonComponent,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxDropdownComponent,
    OnyxMapComponent,
    TranslatePipe,
    AsyncPipe,
    OnyxIconButtonComponent,
    OnyxIconComponent,
  ],
  templateUrl: './onyx-address-picker-modal.component.html',
  styleUrl: './onyx-address-picker-modal.component.scss',
})
export class OnyxAddressPickerModalComponent implements AfterViewInit, OnInit {
  protected readonly data = inject<OnyxAddressPickerModalData>(DIALOG_DATA);
  private readonly dialogRef = inject(DialogRef<OnyxCustomAddress | null>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly addressService = inject(OnyxAddressService);
  private readonly dictionariesService = inject(OnyxDictionariesService);
  private readonly injector = inject(Injector);

  protected readonly I18N = `${I18N_NAMESPACE}.addressPicker`;
  protected readonly I18N_COMMON = `${I18N_NAMESPACE}.common`;
  protected readonly CONTEXT_MENU_OPTIONS: OnyxMapContextMenuOptions[] = [
    {
      id: '1.custom',
      options: [
        {
          name: `${this.I18N}.setLocation`,
          value: (coordinates) =>
            this.addressService
              .geocode(
                [OnyxAddressType.CUSTOM],
                CoordinatesHelper.toString(coordinates),
                1,
              )
              .subscribe({
                next: (result) => {
                  const address = result.items.at(0);
                  this.form.setValue({
                    street: address?.street ?? null,
                    houseNumber: address?.houseNumber ?? null,
                    apartmentNumber: address?.apartmentNumber ?? null,
                    zipCode: address?.zipCode ?? null,
                    city: address?.city ?? null,
                    countryCode: address?.countryCode ?? null,
                    coordinates: CoordinatesHelper.toString(coordinates),
                  });
                },
              }),
          leftIcon: {
            name: 'location-filled',
            size: 20,
          },
          leftIconColor: 'blue',
        },
      ],
    },
  ];

  protected readonly countries$ =
    this.dictionariesService.getDictionary(I18N_COUNTRY);

  protected close$ = new Subject<OnyxCustomAddress | null>();
  protected form = this.buildForm();

  protected coordinatesString = computed(() =>
    CoordinatesHelper.toString(this.coordinates()),
  );

  private coordinates = toSignal(
    this.form.controls.coordinates.valueChanges.pipe(
      startWith(this.form.controls.coordinates.value),
      map((value) => (value ? CoordinatesHelper.fromString(value) : null)),
      takeUntilDestroyed(this.destroyRef),
    ),
  );
  private map = viewChild.required(OnyxMapComponent);

  public ngOnInit(): void {
    const coordinates = this.data.address?.coordinates;
    this.form.setValue({
      street: this.data.address?.street ?? null,
      houseNumber: this.data.address?.houseNumber ?? null,
      apartmentNumber: this.data.address?.apartmentNumber ?? null,
      zipCode: this.data.address?.zipCode ?? null,
      city: this.data.address?.city ?? null,
      countryCode: this.data.address?.countryCode ?? null,
      coordinates: coordinates ? CoordinatesHelper.toString(coordinates) : null,
    });

    if (this.data.readonly) this.form.disable();
  }

  public ngAfterViewInit(): void {
    effect(
      () => {
        const MARKER_ID = 'marker';

        const coordinates = this.coordinates();
        if (coordinates) {
          this.map().dispatch(
            new OnyxMapEvent.AddUpdateMarker({
              id: MARKER_ID,
              type: OnyxMapMarkerType.POINT,
              coordinates,
            }),
          );
        } else {
          this.map().dispatch(new OnyxMapEvent.RemoveMarker({ id: MARKER_ID }));
        }

        this.map().dispatch(new OnyxMapEvent.FitContent());
      },
      { injector: this.injector },
    );
  }

  protected copyCoordinates(): void {
    const coordinates = this.coordinatesString();
    if (coordinates) ActionHelper.copy(coordinates, this.toastService);
  }

  protected clear(): void {
    FormHelper.reset(this.form);
  }

  protected cancel(): void {
    this.close$.next(
      this.data.address?.type === OnyxAddressType.CUSTOM
        ? this.data.address
        : null,
    );
  }

  protected save(): void {
    if (this.form.invalid) {
      FormHelper.submit(this.form);
      this.toastService.showCustom(OnyxToastMessage.INVALID_DATA);
      return;
    }

    const value = this.form.getRawValue() as FormAddress;
    const address = {
      ...value,
      type: OnyxAddressType.CUSTOM as const,
      label: AddressHelper.composeLabel(value),
      coordinates: this.coordinates()!,
      name: null,
    };

    this.close$.next(address);
  }

  protected close(address?: OnyxCustomAddress | null): void {
    this.dialogRef.close(address);
  }

  private buildForm() {
    return this.fb.group({
      street: this.fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      houseNumber: this.fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      apartmentNumber: this.fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      zipCode: this.fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      city: this.fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      countryCode: this.fb.control<string | null>(null, [Validators.required]),
      coordinates: this.fb.control<string | null>(null, [
        Validators.required,
        onyxCoordinatesValidator,
      ]),
    });
  }
}
